import { PublicClientApplication, AuthenticationResult, RedirectRequest } from '@azure/msal-browser';
import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAnalytics, Events, useQueryParams } from '@hooks';
import { getOrCreateSessionId, setSessionUserId } from '../utils/sentryTrackingConfig';
import { setLogoutEventsSent } from '../utils/logoutTrackingGuard';
import { useAuthToken } from './useAuthToken';
import { logger } from '../utils/logger';
import { persistIdpV2Tokens, socialLoginWithIdpV2 } from '../utils/idpV2Auth';

const msalConfig = {
  auth: {
    clientId: '92b224c7-fd7e-42e2-9c68-597d31d82814',
    authority: 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize',
    redirectUri: `${window.origin}/login` // Adjust accordingly for your app
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false
  }
};

// Create an instance of the MSAL application
const msalInstance = new PublicClientApplication(msalConfig);

export default function useMicrosoftAuth(type: 'Login' | 'Registration') {
  const [isInitialized, setInitialized] = useState<boolean>(false);
  const [microsoftAuthLoading, setMicrosoftAuthLoading] = useState(false);
  const [error, setError] = useState<Error>();
  const { setAuthToken, removeAuthToken } = useAuthToken();
  const { push } = useHistory();
  const params = useQueryParams();
  const returnUrl = params.get('returnUrl');
  const { search } = useLocation();
  const [microsoftUser, setMicrosoftUser] = useState<{accessToken: string; email: string; family_name: string, given_name: string}>()

  const { identify, trackEvent, segment } = useAnalytics();

  useEffect(() => {
    // Initialize the MSAL instance
    const initializeMsal = async () => {
      try {
        await msalInstance.initialize(); // Assuming the initialize method exists; if not, remove this line.
        setInitialized(true);
      } catch (error) {
        logger.error('Failed to initialize MSAL:', error);
      }
    };

    initializeMsal();

    // Handling redirect promise if there's a redirect from Microsoft login
    if (isInitialized) {
      msalInstance.handleRedirectPromise().then((tokenResponse: AuthenticationResult | null) => {
        if (tokenResponse) {
          // Handle the response from a redirect flow
        }
      }).catch((error: any) => {
        logger.error('Redirect error:', error);
      });
    }
  }, [isInitialized]);

  // Login function
  const signIn = async () => {
    if (!isInitialized) {
      logger.error('MSAL is not initialized, please wait.');
      return;
    }

    const loginRequest: RedirectRequest = {
      scopes: ['user.read'],  // This scope requests access to read the user's profile
      prompt: 'select_account'
    };

    try {
      // Uses loginPopup method, could also use loginRedirect for redirect behavior
      const loginResponse: AuthenticationResult = await msalInstance.loginPopup(loginRequest);
      setMicrosoftUser({
        accessToken: loginResponse.accessToken,
        email: loginResponse.account.username,
        family_name: loginResponse.account.name?.split(' ')[0] ?? '',
        given_name: loginResponse.account.name?.split(' ')[1] ?? ''
      })
      if (type === 'Login') {
        setMicrosoftAuthLoading(true)
        setError(undefined);
        const socialLogin = await socialLoginWithIdpV2('Microsoft', loginResponse.accessToken);
        if (socialLogin.access_token) {
          setLogoutEventsSent(false);
          removeAuthToken();
          persistIdpV2Tokens(socialLogin);
          setAuthToken(socialLogin.access_token, true);
          if (socialLogin.user_id) {
            segment?.identify(socialLogin.user_id);
            identify(socialLogin.user_id, { email: socialLogin.email });
          }
          const sessionId = getOrCreateSessionId();
          const timestamp = new Date().toISOString();
          const d = { email: socialLogin.email, loginType: 'Microsoft', userId: socialLogin.user_id, sessionId, timestamp };
          trackEvent(Events.actions.logIn, d);
          trackEvent(Events.actions.sessionStarted, d);
          if (socialLogin.user_id) setSessionUserId(socialLogin.user_id);
          if (returnUrl && search.replace('?returnUrl=', '').includes(returnUrl)) {
            push(search.replace('?returnUrl=', '').replace(`${returnUrl}&`, `${returnUrl}?`));
          } else {
            push('/');
          }
        } else if (socialLogin.mfa_required && socialLogin.challenge_id && (socialLogin.method === 'totp' || socialLogin.method === 'totp_setup')) {
          const state: any = { idpChallengeId: socialLogin.challenge_id, idpMethod: socialLogin.method, email: socialLogin.email };
          if (socialLogin.method === 'totp_setup' && socialLogin.uri) state.totpUri = socialLogin.uri;
          push('/verify', state);
        } else if (socialLogin.mfa_required && socialLogin.challenge_id && socialLogin.method === 'email_otp') {
          push('/admin/verify', { idpChallengeId: socialLogin.challenge_id, idpMethod: socialLogin.method, email: socialLogin.email });
        } else {
          throw new Error('Login attempt failed');
        }
      }
    } catch (error) {
      const loginError = error instanceof Error ? error : new Error('Login attempt failed');
      setError(loginError);
      trackEvent(Events.actions.logInFailed, {
        loginType: 'Microsoft',
        clientId: localStorage.getItem('clientId'),
        message: 'Login attempt failed',
        error: loginError.message,
      });
      logger.error('Login failed:', error);
    } finally {
      setMicrosoftAuthLoading(false)
    }
  };

  return {
    microsoftAuth: signIn,
    microsoftUser,
    microsoftAuthLoading,
    microsoftError: error,
    setMicrosoftUser
  }
}
